import React, { useState, useEffect } from "react";
import "../../Styles/StudentAutonomousForm.css";
import { FaEye, FaEyeSlash, FaSyncAlt } from "react-icons/fa";

import ApiService from "../../nhrc_apis/ApiService";

const { MembershipAPI, UploadAPI, AuthAPI } = ApiService;

function StudentAutonomousForm({
  personalData,
  memberId,
  membershipId,
  onBackToPersonal,
}) {

  const [formData, setFormData] = useState({
    collegeName: "",
    collegeCode: "",
    qualification: "",
    department: "",
    startYear: "",
    endYear: "",
    location: "",
    idFront: null,
    idBack: null,
    email: "",
    password: "",
    confirmPassword: "",
    puzzleAnswer: "",
  });

  /* ================= CAPTCHA STATE ================= */
  const [captcha, setCaptcha] = useState({
    id: null,
    question: "",
  });

  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaValid, setCaptchaValid] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  /* ---------------- AUTO EMAIL ---------------- */
  useEffect(() => {
    if (personalData?.email) {
      setFormData((prev) => ({
        ...prev,
        email: personalData.email,
      }));
    }
  }, [personalData]);

  /* ================= LOAD CAPTCHA ================= */
  const loadCaptcha = async () => {
    try {
      const res = await AuthAPI.getCaptcha();

      setCaptcha({
        id: res?.data?.captcha_id || res?.data?.id,
        question: res?.data?.question,
      });

      setCaptchaInput("");
      setCaptchaValid(null);

    } catch (err) {
      console.error("CAPTCHA LOAD ERROR:", err);
      alert("Failed to load captcha");
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  /* ================= VERIFY CAPTCHA ================= */
  const verifyCaptcha = async () => {
    if (!captcha.id) {
      alert("Captcha not loaded, click refresh");
      return;
    }

    try {
      setVerifying(true);

      const res = await AuthAPI.verifyCaptcha({
        captcha_id: captcha.id,
        answer: captchaInput,
      });

      // backend returns message if success
      setCaptchaValid(true);

    } catch (err) {
      console.error("CAPTCHA VERIFY ERROR:", err);
      setCaptchaValid(false);
      alert(err?.response?.data?.detail || "❌ Captcha incorrect");
    } finally {
      setVerifying(false);
    }
  };

  /* ---------------- INPUT ---------------- */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- UPLOAD ---------------- */
  const uploadFile = async (file) => {
    if (!file) return "";

    const fd = new FormData();
    fd.append("file", file);
    fd.append("membership_id", membershipId);
    fd.append("file_type", "student");

    const res = await UploadAPI.uploadFile(fd);

    return (
      res.data?.fileUrl ||
      res.data?.url ||
      res.data?.file_path ||
      ""
    );
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValid) {
      alert("❌ Please verify captcha first");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert("❌ Password mismatch");
      return;
    }

    try {
      setLoading(true);

      const frontUrl = await uploadFile(formData.idFront);
      const backUrl = await uploadFile(formData.idBack);

      const payload = {
        member_id: memberId,
        membership_id: membershipId,

        college_name: formData.collegeName,
        college_code: formData.collegeCode,
        qualification: formData.qualification,
        department: formData.department,
        start_year: Number(formData.startYear),
        end_year: Number(formData.endYear),
        location: formData.location,

        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,

        captcha_id: captcha.id,
        captcha_answer: captchaInput,

        id_front: frontUrl,
        id_back: backUrl,
      };

      console.log("FINAL PAYLOAD:", payload);

      const res = await MembershipAPI.studentAutonomous(payload);

      setStudentId(res.data?.id || "Generated");
      setShowPopup(true);

    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || "❌ API Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stu-autonomous-wrapper123">

      <h3 className="stu-autonomous-title123">
        STUDENT AUTONOMOUS DETAILS
      </h3>

      <div className="stu-autonomous-card123">

        <form className="stu-autonomous-form123" onSubmit={handleSubmit}>

          <div className="stu-field-group123">
            <label className="stu-label123">College Name<span className="stu-required123">*</span></label>
            <input name="collegeName" placeholder="College Name"
              value={formData.collegeName} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">College Code<span className="stu-required123">*</span></label>
            <input name="collegeCode" placeholder="College Code"
              value={formData.collegeCode} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Qualification<span className="stu-required123">*</span></label>
            <input name="qualification" placeholder="Qualification"
              value={formData.qualification} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Department<span className="stu-required123">*</span></label>
            <input name="department" placeholder="Department"
              value={formData.department} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Start Year<span className="stu-required123">*</span></label>
            <input name="startYear" type="number"
              placeholder="Start Year"
              value={formData.startYear} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">End Year<span className="stu-required123">*</span></label>
            <input name="endYear" type="number"
              placeholder="End Year"
              value={formData.endYear} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Location<span className="stu-required123">*</span></label>
            <input name="location" placeholder="Location"
              value={formData.location} onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">ID Proof Front Side<span className="stu-required123">*</span></label>
            <input type="file" name="idFront" onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">ID Proof Back Side<span className="stu-required123">*</span></label>
            <input type="file" name="idBack" onChange={handleChange} required />
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Email<span className="stu-required123">*</span></label>
            <input type="email" name="email"
              value={formData.email}
              readOnly required />
          </div>

          <div className="stu-field-group123 stu-password-group123">
            <label className="stu-label123">Password<span className="stu-required123">*</span></label>
            <input type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              onChange={handleChange}
              required />
            <span className="stu-eye-toggle123" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="stu-field-group123">
            <label className="stu-label123">Confirm Password<span className="stu-required123">*</span></label>
            <input type={showPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              onChange={handleChange}
              required />
          </div>

          {/* ================= CAPTCHA ================= */}
          <div className="stu-captcha-box123">

            <label className="stu-label123" style={{ color: "black" }}>
              Captcha Verification<span className="stu-required123">*</span>
            </label>

            <p className="stu-captcha-question123">{captcha.question || "Loading captcha..."}</p>

            <input
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              required
            />

            <button type="button" className="stu-captcha-refresh-btn123" onClick={loadCaptcha}>
              <FaSyncAlt />
            </button>

            <button type="button" className="stu-captcha-verify-btn123" onClick={verifyCaptcha} disabled={verifying}>
              {verifying ? "Verifying..." : "Verify"}
            </button>

            {captchaValid === true && <span className="stu-result-icon123 stu-correct123">✔</span>}
            {captchaValid === false && <span className="stu-result-icon123 stu-wrong123">✖</span>}

          </div>

          <div className="stu-form-buttons123">
            <button type="button" className="stu-back-btn123" onClick={onBackToPersonal}>
              Back
            </button>

            <button type="submit" className="stu-register-btn123" disabled={loading}>
              {loading ? "Submitting..." : "Register"}
            </button>
          </div>

        </form>
      </div>

      {showPopup && (
        <div className="stu-popup-overlay123">
          <div className="stu-popup-card123">
            <h2>🎉 Student Registered</h2>
            <p><b>ID:</b> {studentId}</p>
            <p><b>Email:</b> {formData.email}</p>
            <button className="stu-continue-btn123" onClick={onBackToPersonal}>Continue</button>
          </div>
        </div>
      )}

    </div>
  );
}

export default StudentAutonomousForm;